import { resolvePlan } from '@/engine/resolve-plan.js';
import { runStep } from '@/engine/run-step.js';
import {
	applyStep,
	applySyncNav,
	setStepLoading,
	attachPhaseMeta,
	detachPhaseMeta,
	initSnapshotDirtyKeys,
} from '@/engine/apply-step.js';
import { cloneSnapshot, commitSnapshot } from '@/engine/snapshot.js';

/**
 * @typedef {import('@/engine/resolve-plan.js').FilterIntent} FilterIntent
 * @typedef {import('@/engine/filter-graph.js').FilterPlan} FilterPlan
 * @typedef {import('@/engine/filter-graph.js').PlanStep} PlanStep
 * @typedef {import('@/engine/filter-graph.js').StepId} StepId
 * @typedef {import('@/types').PhaseMeta} PhaseMeta
 * @typedef {import('@/types').EngineSnapshot} EngineSnapshot
 * @typedef {import('@/types').EngineContext} EngineContext
 * @typedef {import('@/types').LiveState} LiveState
 * @typedef {import('@/types').StepRuntime} StepRuntime
 * @typedef {import('@/types').SyncNav} SyncNav
 */

/**
 * @param {PlanStep} entry
 * @returns {{ stepId: StepId, meta: PhaseMeta }}
 */
function resolvePlanStep(entry) {
	if (typeof entry === 'string') {
		return { stepId: entry, meta: {} };
	}

	return { stepId: entry.step, meta: entry.meta ?? {} };
}

/**
 * Run plan steps in order: load → fetch → apply → optional syncNav.
 *
 * @param {FilterPlan} plan
 * @param {EngineSnapshot} snapshot
 * @param {EngineContext} context
 * @param {{ signal?: AbortSignal, runtime?: StepRuntime, onStale?: () => boolean }} [options]
 * @returns {Promise<EngineSnapshot>}
 */
async function executePlan(plan, snapshot, context, options = {}) {
	let { signal, runtime = {}, onStale } = options;

	for (const entry of plan.steps) {
		if (onStale?.()) {
			return snapshot;
		}

		let { stepId, meta } = resolvePlanStep(entry);

		attachPhaseMeta(snapshot, meta);
		setStepLoading(snapshot, stepId, true, meta);

		let stepResult = await runStep(stepId, snapshot, {
			signal,
			phaseMeta: meta,
			planMeta: plan.meta ?? {},
			runtime,
		});

		if (onStale?.()) {
			detachPhaseMeta(snapshot);
			return snapshot;
		}

		applyStep(snapshot, stepResult, context);
		detachPhaseMeta(snapshot);

		if (meta.syncNav) {
			applySyncNav(snapshot, /** @type {SyncNav} */ (meta.syncNav), runtime);
		}
	}

	return snapshot;
}

/**
 * Serializes filter intents and executes FILTER_GRAPH plans one generation at a time.
 *
 * @param {{ liveState: LiveState, context?: EngineContext }} config
 */
export function createFilterEngine({ liveState, context = /** @type {EngineContext} */ ({}) }) {
	/** @type {Array<{ intent: FilterIntent, resolve: (value: unknown) => void, reject: (reason?: unknown) => void }>} */
	let queue = [];
	let processing = false;
	let generation = 0;
	/** @type {AbortController | null} */
	let activeController = null;

	/**
	 * @param {FilterIntent} intent
	 * @returns {Promise<unknown>}
	 */
	function dispatch(intent) {
		return new Promise((resolve, reject) => {
			queue.push({ intent, resolve, reject });

			if (processing) {
				generation += 1;
				activeController?.abort();
			}

			drainQueue();
		});
	}

	function coalesceQueueBatch() {
		if (queue.length === 0) {
			return null;
		}

		let batch = queue.splice(0);
		let resolvers = batch.map((item) => ({ resolve: item.resolve, reject: item.reject }));
		let initItem = batch.find((item) => item.intent.type === 'INIT');
		let intent = initItem ? initItem.intent : batch[batch.length - 1].intent;

		return { intent, resolvers };
	}

	function buildSnapshot() {
		return /** @type {EngineSnapshot} */ (cloneSnapshot({
			options: liveState.options,
			selected: liveState.selected,
			results: liveState.data.results,
			categories: liveState.data.categories,
			currentNav: liveState.data.currentNav,
			currentNavResults: liveState.data.currentNavResults,
			resultsLoading: liveState.data.resultsLoading,
			navLoading: liveState.data.navLoading,
			loading: liveState.options.loading,
		}));
	}

	/**
	 * @param {Array<{ resolve: (value: unknown) => void, reject: (reason?: unknown) => void }>} resolvers
	 * @param {(resolve: (value: unknown) => void, reject: (reason?: unknown) => void) => void} handler
	 */
	function settleBatch(resolvers, handler) {
		for (const { resolve, reject } of resolvers) {
			try {
				handler(resolve, reject);
			} catch (error) {
				reject(error);
			}
		}
	}

	async function drainQueue() {
		if (processing) {
			return;
		}
		processing = true;

		try {
			while (queue.length > 0) {
				let batch = coalesceQueueBatch();
				if (!batch) {
					break;
				}

				generation += 1;
				let currentGeneration = generation;

				if (activeController) {
					activeController.abort();
				}
				activeController = new AbortController();

				let { plan } = resolvePlan(batch.intent, {
					lockCollegeFilter: context.lockCollegeFilter,
					options: liveState.options,
					selected: liveState.selected,
				});

				if (!plan) {
					settleBatch(batch.resolvers, (resolve) => resolve(null));
					continue;
				}

				let snapshot = buildSnapshot();
				initSnapshotDirtyKeys(snapshot);

				try {
					let completed = await executePlan(plan, snapshot, context, {
						signal: activeController.signal,
						runtime: batch.intent.runtime ?? {},
						onStale: () => currentGeneration !== generation,
					});

					if (currentGeneration === generation) {
						commitSnapshot(liveState, completed);
						settleBatch(batch.resolvers, (resolve) => resolve(completed));
					} else {
						settleBatch(batch.resolvers, (resolve) => resolve(null));
					}
				} catch (error) {
					let err = /** @type {{ name?: string, code?: string }} */ (error);
					if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
						settleBatch(batch.resolvers, (resolve) => resolve(null));
						continue;
					}
					context.onError?.(error);
					settleBatch(batch.resolvers, (_, reject) => reject(error));
				}
			}
		} finally {
			processing = false;
			if (queue.length > 0) {
				drainQueue();
			}
		}
	}

	return {
		dispatch,
		get generation() {
			return generation;
		},
	};
}
